import React, { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Layout Context for managing layout-related settings across the application
 * Following Module M5 (State Management) principles
 * 
 * This context allows child components to communicate layout requirements
 * to parent layout components, enabling proper alignment without tight coupling.
 */

interface LayoutContextType {
  /**
   * The total horizontal padding/inset for page content
   * This includes MainContent padding + any page-specific padding
   */
  contentInset: string;
  
  /**
   * Mobile-specific content inset
   */
  mobileContentInset: string;
  
  /**
   * Update the content inset value
   */
  setContentInset: (inset: string) => void;
  
  /**
   * Update the mobile content inset value
   */
  setMobileContentInset: (inset: string) => void;
  
  /**
   * Predefined inset values for consistency
   */
  insets: {
    default: string;
    compact: string;
    comfortable: string;
    spacious: string;
  };
  
  /**
   * Mobile-specific inset values
   */
  mobileInsets: {
    default: string;
    compact: string;
    comfortable: string;
    spacious: string;
  };
}

// Default inset values following design system principles
const defaultInsets = {
  default: 'var(--spacing-lg)', // Just MainContent padding (24px)
  compact: 'calc(var(--spacing-lg) + var(--spacing-sm))', // 24px + 8px = 32px
  comfortable: 'calc(var(--spacing-lg) + 1rem)', // 24px + 16px = 40px
  spacious: 'calc(var(--spacing-lg) + var(--spacing-lg))', // 24px + 24px = 48px
};

// Mobile-specific inset values
const defaultMobileInsets = {
  default: 'var(--spacing-md)', // Just MainContent mobile padding (16px)
  compact: 'calc(var(--spacing-md) + var(--spacing-sm))', // 16px + 8px = 24px
  comfortable: 'calc(var(--spacing-md) + 1rem)', // 16px + 16px = 32px
  spacious: 'calc(var(--spacing-md) + var(--spacing-md))', // 16px + 16px = 32px
};

const LayoutContext = createContext<LayoutContextType>({
  contentInset: defaultInsets.default,
  mobileContentInset: defaultMobileInsets.default,
  setContentInset: () => {},
  setMobileContentInset: () => {},
  insets: defaultInsets,
  mobileInsets: defaultMobileInsets,
});

interface LayoutProviderProps {
  children: ReactNode;
}

/**
 * Layout Provider Component
 * Manages layout state for the entire application
 */
export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const [contentInset, setContentInset] = useState(defaultInsets.default);
  const [mobileContentInset, setMobileContentInset] = useState(defaultMobileInsets.default);

  const value: LayoutContextType = {
    contentInset,
    mobileContentInset,
    setContentInset,
    setMobileContentInset,
    insets: defaultInsets,
    mobileInsets: defaultMobileInsets,
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

/**
 * Hook to access layout context
 * @throws Error if used outside of LayoutProvider
 */
export const useLayout = () => {
  const context = useContext(LayoutContext);
  
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  
  return context;
};

/**
 * Higher-order component to provide content inset to a page component
 * This follows Module M2 (Component Design) by enhancing components without modifying them
 */
export const withContentInset = (desktopInset: string, mobileInset?: string) => {
  return <P extends object>(Component: React.ComponentType<P>): React.FC<P> => {
    return (props: P) => {
      const { setContentInset, setMobileContentInset } = useLayout();
      
      React.useEffect(() => {
        setContentInset(desktopInset);
        if (mobileInset) {
          setMobileContentInset(mobileInset);
        }
        
        // Reset to default when component unmounts
        return () => {
          setContentInset(defaultInsets.default);
          setMobileContentInset(defaultMobileInsets.default);
        };
      }, [setContentInset, setMobileContentInset, desktopInset, mobileInset]);
      
      return <Component {...props} />;
    };
  };
};

export default LayoutContext;